import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
	faPencilAlt,
	faClose,
	faAngleDown,
	faCheckSquare,
	faEye,
	faMagnifyingGlass,
	faPencil,
	faSortDown,
	faSortUp,
	faSquare,
	faTrash,
	faWrench,
	faCheck,
	faClock,
	faPlus,
	faAngleLeft,
	faAngleRight,
} from '@fortawesome/free-solid-svg-icons';
import { genericFormI } from 'src/app/util/interfaces/genericFormRMK';
import { DateService } from 'src/app/services/date/date.service';
import { ciInterface2 } from 'src/app/util/interfaces/ci';
import { Subscription } from 'rxjs';
import { PermissionService } from 'src/app/util/services/permission/permission.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { Router } from '@angular/router';
import { InconsistenciesCiService } from '../inconsistencies-ci-list/data/inconsistencies-ci.service';
import { CommonModule } from '@angular/common';
import { TooltipDirective } from 'src/app/util/directives/tooltip/tooltip.directive';
import { PaginationCounterModule } from 'src/app/page_component_item/pagination-counter/pagination-counter.module';
import { SvgGearsModule } from 'src/app/page_component_item/svg-gears/svg-gears.module';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MspPipesModule } from 'src/app/util/pipes/msp-pipes.module';

@Component({
	standalone: true,
	imports: [CommonModule, TooltipDirective, PaginationCounterModule, SvgGearsModule, FontAwesomeModule, MspPipesModule],
	selector: 'cpi-table-generic-filters',
	templateUrl: './table-generic-filters.component.html',
	styleUrls: ['./table-generic-filters.component.scss', '../../../../../page/cmdb/cmdb-home-screen/cmdb-home-screen.component.scss'],
})
export class TableGenericFiltersComponent implements OnInit {
	private readonly InconsistenciesCiService = inject(InconsistenciesCiService);
	private readonly NotificationService = inject(NotificationService);

	// ============= LABELS E INTERFACE =============
	@Input() labels = {
		// Usado em: handleCellValue(), confirmButton() - Dicionário de labels para internacionalização
		lbl_adicionar_ativo: '',
		lbl_edocs_carregando: 'Loading',
		lbl_geral: '',
		lbl_geral_ativos_em_uso: '',
		lbl_geral_ativos_em_estoque: '',
		lbl_geral_selecione: '',
		lbl_asset_tipo_ci: '',
		lbl_geral_mes: '',
		lbl_geral_dia: '',
		lbl_geral_semana: '',
		lbl_geral_localidade: '',
		lbl_geral_exportar: '',
		lbl_geral_ver: '',
		lbl_geral_detalhes: '',
		lbl_geral_movimentacoes_ativos_periodo_tempo: '',
		lbl_geral_inconsistencias: '',
		lbl_geral_ver_detalhes: '',
		lbl_geral_my_asset: 'MyAsset',
		lbl_essa_extracao_pode_levar_segundos: '',
		lbl_extracao_geral: '',
		lbl_geral_todos: '',
		lbl_localidade: '',
		lbl_geral_relatorio: '',
		lbl_geral_selecionar_todos: '',
		lbl_bulletin_actions: 'Ações',
		lbl_clique_confirmar_acao: '',
		lbl_limpar_filtros: '',
		lbl_marcar_inconsistencia_nao_resolvida: '',
		lbl_marcar_inconsistencia_resolvida: '',
		lbl_geral_remover: '',
		lbl_editar_item: '',
	};

	icons = {
		// Usado em: template HTML - Ícones FontAwesome para botões e interface
		faSquare: faSquare,
		faCheckSquare: faCheckSquare,
		faAngleDown: faAngleDown,
		faSortDown: faSortDown,
		faPencil: faPencil,
		faEye: faEye,
		faMagnifyingGlass: faMagnifyingGlass,
		faSortUp: faSortUp,
		faTrash: faTrash,
		faWrench: faWrench,
		faCheck: faCheck,
		faClock: faClock,
		faPlus: faPlus,
		faAngleLeft: faAngleLeft,
		faAngleRight: faAngleRight,
		faClose: faClose,
		faPencilAlt: faPencilAlt,
	};

	// ============= INPUTS/OUTPUTS =============
	@Input() idInconsistency: string;
	@Input() ciTableArr = []; // Usado em: template HTML, applyPagination() - Array principal de dados recebido do componente pai
	@Input() ciTableHeader = []; // Usado em: template HTML, setSearchParam() - Cabeçalhos da tabela com configurações de colunas
	@Input() selectedCiTableIndexArr: number[] = []; // Usado em: selectCi(), deselectCi(), clearPageSelections() - Índices dos itens selecionados na página atual
	@Input() selectedCiTableArr: ciInterface2[] = []; // Usado em: selectCi(), deselectCi(), deleteSelectedCis() - Array com objetos completos dos itens selecionados
	@Input() search = {
		// Usado em: filterSearchOptions(), setSearchParam() - Objeto com parâmetros de busca e filtros
		general: '',
		field: [],
		values: [],
		filter: [],
	};

	@Input() externalItem?: any;
	@Input() isDisabled = false;

	@Output() closeView = new EventEmitter<void>(); // Usado em: goBackToDashboard() - Evento emitido para fechar a view atual
	@Output() onSearchTable = new EventEmitter<{
		// Usado em: searchCITable() - Evento emitido para solicitar busca ao componente pai
		page: number;
		search: any;
		sortBy: string;
		sortDown: boolean;
		recount: boolean;
		pageSize?: string;
		show_mine?: boolean;
	}>();
	@Output() onItemsDeleted = new EventEmitter<ciInterface2>(); // Usado em: deleteSelectedCis() - Evento emitido quando itens são removidos

	// ============= ARRAYS DE DADOS =============
	fullDataArr = []; // Usado em: updateTableData(), deleteSelectedCis(), applyPagination() - Array completo de dados sem paginação
	paginatedDataArr = []; // Usado em: template HTML, applyPagination(), selectCi(), deselectCi() - Array com dados da página atual (paginado)
	selectedInconsistencyTableIndexArr: number[] = []; // Usado em: (não usado atualmente) - Índices selecionados da tabela de inconsistências
	selectedInconsistencyTableArr: any[] = []; // Usado em: (não usado atualmente) - Array de inconsistências selecionadas

	// ============= CONTROLE DE ESTADO/LOADING =============
	isLoadingLabels = true; // Usado em: (inicializado mas não usado) - Estado de carregamento de labels
	isLoadingCiTable = false; // Usado em: template HTML, searchCITable(), onSearchComplete() - Estado de carregamento da tabela
	isLoadingCiTableHeader = true; // Usado em: (inicializado mas não usado) - Estado de carregamento do cabeçalho
	isFirstLoad = true; // Usado em: onSearchComplete() - Controla se é a primeira carga de dados
	isImpactMapOpen = false; // Usado em: (inicializado mas não usado) - Controla se o mapa de impacto está aberto
	debugImport = false; // Usado em: constructor() - Flag para debug (exposta no console)
	isTogglingSolved: string[] = [];

	// ============= PAGINAÇÃO E ORDENAÇÃO =============
	itemsPerPage = 20; // Usado em: applyPagination(), searchCITable() - Número de itens por página (fixo em 20)
	ciTablePage = 1; // Usado em: searchCITable(), applyPagination(), deleteSelectedCis() - Página atual da tabela
	ciTableCount = 1; // Usado em: applyPagination() - Total de registros (sem considerar paginação)
	sortBy = 'id'; // Usado em: sortColumn(), searchCITable() - Campo usado para ordenação
	sortDown = false; // Usado em: sortColumn(), searchCITable() - Direção da ordenação (true = descendente)

	// ============= CONTROLE DE BUSCA E FILTROS =============
	recount = true; // Usado em: openDropdown(), setSearchParam(), setAllSearchParams() - Força recontagem nos resultados
	show_mine = false; // Usado em: searchCITable() - Controla exibição apenas dos próprios itens
	searchReq: Subscription; // Usado em: constructor() - Subscription para controle de requisições de busca
	dropdown = {
		// Usado em: openDropdown(), onSearchComplete() - Controle do dropdown de filtros
		name: '',
		x: 0,
		y: 0,
	};

	// ============= CONTROLE DE NAVEGAÇÃO =============
	mainMenu = 'edit'; // Usado em: (inicializado mas não usado) - Menu principal ativo
	tabView = 'info'; // Usado em: (inicializado mas não usado) - Aba ativa na visualização

	constructor(
		private DateService: DateService,
		public PermissionService: PermissionService,
		private router: Router
	) {
		this.filterSearchOptions = this.filterSearchOptions.bind(this);
		// Expose to console
		(window as any).debugToggle = (value: boolean) => {
			this.debugImport = value;
		};
	}
	ngOnInit() {
		// Inicializa paginação com dados atuais
		if (this.ciTableArr.length > 0) {
			this.fullDataArr = [...this.ciTableArr];
			this.applyPagination();
		}
	}

	goBackToDashboard(): void {
		// 3. Emita o evento para notificar o componente pai.
		this.closeView.emit();
	}

	// selectAllValues() {
	//   //Abre o dropdown ao clicar na coluna para filtrar
	//   console.log('Botão para Seleciona todos os items da lista', this.selectedCiTableIndexArr.length != this.itemsPerPage)
	//   this.selectedCiTableIndexArr = [];

	//   if (this.selectedCiTableIndexArr.length != this.itemsPerPage) {
	//     console.log(this.selectedCiTableIndexArr.length != this.itemsPerPage)
	//     for (let i = 0; i < this.ciTableArr.length; i++) {
	//       this.selectedCiTableIndexArr.push(i);
	//       this.selectedCiTableArr.push(this.ciTableArr[i]);
	//     }
	//   }
	//   console.log(this.selectedCiTableIndexArr.length != this.itemsPerPage)
	// }

	openDropdown(name: string, mouseEvent: MouseEvent): void {
		//Abre o dropdown ao clicar na coluna para filtrar
		console.log('openDropdown: Abre o dropdown ao clicar na coluna para filtrar', name);
		if (this.dropdown.name == name) {
			this.dropdown.name = '';
			if (this.recount == true) this.searchCITable(1);
			return;
		}
		this.dropdown.name = name;
		// this.dropdown.x = mouseEvent.clientX - 108; //108 to fix
		// this.dropdown.y = mouseEvent.clientY - 55; //55 to fix
	}

	filterSearchOptions(loopItem: any, headerIndex: number): boolean {
		const allEmpty = this.search.filter.find((v) => {
			return v != '';
		});
		if (allEmpty == undefined) return true;

		let searchParameter = this.search.filter[headerIndex];
		if (!loopItem || (headerIndex && searchParameter == undefined) || searchParameter == '') return false;

		let valueLabel = loopItem.label;
		const noWhiteSpaceValueLabel = valueLabel.replaceAll(/\s+/g, '');
		searchParameter = searchParameter.trim();
		if (this.labels[loopItem.label] != undefined) valueLabel = this.labels[loopItem.label];

		return (
			valueLabel.toLowerCase().includes(searchParameter.toLowerCase()) ||
			noWhiteSpaceValueLabel.toLowerCase().includes(searchParameter.toLowerCase())
		);
	}

	sortColumn(sortBy: string) {
		if (this.sortBy == sortBy) {
			this.sortDown = !this.sortDown;
		} else {
			this.sortBy = sortBy;
			this.sortDown = true;
		}
	}

	searchCITable(page: number): void {
		//Função para buscar o filtro que está selecionado no select ou o que está being digitado
		//console.log('searchCITable: Função para buscar o filtro que está selecionado no select ou o que está sendo digitado', page)
		const cisTableContainerElement = document.querySelector('#cisTableContainer');
		if (cisTableContainerElement != undefined) {
			cisTableContainerElement.scrollTop = 0;
			cisTableContainerElement.scrollLeft = 0;
		}

		this.isLoadingCiTable = true;
		this.ciTablePage = page;

		// Emite evento para o componente pai fazer a busca
		this.onSearchTable.emit({
			page: page,
			search: this.search,
			sortBy: this.sortBy,
			sortDown: this.sortDown,
			recount: this.recount,
			pageSize: this.itemsPerPage.toString(),
			show_mine: this.show_mine,
		});

		// Aplica paginação local nos dados atuais
		this.applyPagination();
	}

	// Método para aplicar paginação nos dados
	applyPagination(): void {
		const startIndex = (this.ciTablePage - 1) * this.itemsPerPage;
		const endIndex = startIndex + this.itemsPerPage;

		// Se fullDataArr estiver vazio, usa ciTableArr
		const dataSource = this.fullDataArr.length > 0 ? this.fullDataArr : this.ciTableArr;

		this.paginatedDataArr = dataSource.slice(startIndex, endIndex);
		this.ciTableCount = dataSource.length;

		// Limpa seleções da página atual quando aplica nova paginação
		this.clearPageSelections();
	}

	// Método para ser chamado pelo componente pai quando a busca terminar
	onSearchComplete(data: any) {
		this.resetSearchSort();
		this.isLoadingCiTable = false;
		this.recount = false;

		if (this.isFirstLoad) {
			this.isFirstLoad = false;
		}
		this.dropdown.name = '';

		// Aplica paginação após receber novos dados
		this.applyPagination();
	}

	// Método para atualizar dados e aplicar paginação
	updateTableData(newData: any[]): void {
		this.fullDataArr = [...newData];
		this.ciTableArr = [...newData];
		this.applyPagination();
	}

	// Método para limpar seleções ao mudar de página
	clearPageSelections(): void {
		this.selectedCiTableIndexArr = [];
		// Mantém apenas seleções de outras páginas no selectedCiTableArr se necessário
	}

	resetSearchSort(): void {
		this.selectedCiTableIndexArr = [];
		this.selectedCiTableArr = [];
		this.ciTableHeader.forEach(() => {
			this.search.filter.push('');
		});
	}

	// Método para limpar completamente todas as seleções e filtros
	clearAllSelectionsAndFilters(): void {
		// Limpa seleções
		this.selectedCiTableIndexArr = [];
		this.selectedCiTableArr = [];

		// Limpa filtros e valores de busca
		this.search.general = '';
		this.search.filter = [];
		this.search.values = [];

		// Reinicializa arrays de filtros baseado no cabeçalho
		this.ciTableHeader.forEach(() => {
			this.search.filter.push('');
			this.search.values.push([]);
		});

		// Reinicializa valores de string para campos apropriados
		for (const [index, header] of this.ciTableHeader.entries()) {
			if (header.inputType == 'string') {
				this.search.values[index] = [''];
			}
		}

		// Força recontagem e reaplica paginação
		this.recount = true;
		this.dropdown.name = '';
		this.applyPagination();
	}

	setSearchParam(fieldIndex: number, parameter: string): void {
		//Função para buscar os subtipos para filtragem (setSearchParam)
		console.log('setSearchParam Função para buscar os subtipos para filtragem', fieldIndex, parameter);
		this.recount = true;
		if (this.ciTableHeader[fieldIndex].inputType == 'string' || this.ciTableHeader[fieldIndex].inputType == 'date') {
			this.search.values[fieldIndex][0] = parameter;
		}

		if (
			this.ciTableHeader[fieldIndex].inputType == 'select' ||
			this.ciTableHeader[fieldIndex].inputType == 'select-iac' ||
			this.ciTableHeader[fieldIndex].inputType == 'boolean'
		) {
			if (this.search.values[fieldIndex].includes(parameter))
				this.search.values[fieldIndex].splice(this.search.values[fieldIndex].indexOf(parameter), 1);
			else this.search.values[fieldIndex].push(parameter);
		}
	}

	setAllSearchParams(fieldIndex: number): void {
		//Função para buscar todos parametros do filtro (setAllSearchParams)
		console.log('setAllSearchParams, Função para buscar todos parametros do filtro', fieldIndex);
		this.recount = true;
		if (this.search.values[fieldIndex].length == this.ciTableHeader[fieldIndex].items.length) {
			this.search.values[fieldIndex] = [];
		} else {
			this.search.values[fieldIndex] = [];
			for (const item of this.ciTableHeader[fieldIndex].items) {
				this.search.values[fieldIndex].push(item.values[0]);
			}
		}
	}

	handleCellValue(cellValue: any, cellHeaderInputType: genericFormI['inputType']): string {
		cellValue = this.separateCellMarkupFromValue(cellValue, 'cellValue');
		if (cellHeaderInputType == 'date') return this.DateService.formatDate(cellValue);
		else {
			return this.labels[cellValue] != undefined && this.labels[cellValue] != '' ? this.labels[cellValue] : cellValue;
		}
	}

	handleCellMarkup(cellValue: any): string {
		cellValue = this.separateCellMarkupFromValue(cellValue, 'cellMarkup');
		return cellValue;
	}

	isElementOverflown(element: HTMLElement) {
		return element.scrollWidth > element.clientWidth;
	}

	editItem(index: number): void {
		const item = this.ciTableArr[index];
		console.log(item);
	}

	selectCi(index: number): void {
		if (!this.selectedCiTableIndexArr.includes(index)) {
			this.selectedCiTableIndexArr.push(index);

			// Pega o item do array paginado
			const selectedItem = this.paginatedDataArr[index];
			if (selectedItem && !this.selectedCiTableArr.some((item) => item.id === selectedItem.id)) {
				this.selectedCiTableArr.push(selectedItem);
			}
		}
	}

	deselectCi(index: number): void {
		// Remove do array de índices selecionados
		const indexPos = this.selectedCiTableIndexArr.indexOf(index);
		if (indexPos > -1) {
			this.selectedCiTableIndexArr.splice(indexPos, 1);
		}

		// Remove do array de itens selecionados
		const selectedItem = this.paginatedDataArr[index];
		if (selectedItem) {
			const itemIndex = this.selectedCiTableArr.findIndex((item) => item.id === selectedItem.id);
			if (itemIndex > -1) {
				this.selectedCiTableArr.splice(itemIndex, 1);
			}
		}
	}

	deleteCI(idCI: string) {
		// Guarda os itens que serão removidos para emitir o evento
		const itemToDelete = this.ciTableArr.find((item) => item.id === idCI);
		if (!itemToDelete) return;

		// Emite evento para o componente pai com os itens removidos
		this.onItemsDeleted.emit(itemToDelete);

		// Reaplica a paginação
		this.applyPagination();

		// Se a página atual ficou vazia, volta para a página anterior
		if (this.paginatedDataArr.length === 0 && this.ciTablePage > 1) {
			this.ciTablePage--;
			this.applyPagination();
		}
	}

	openCiContextMenu(e: MouseEvent): void {}

	checkPermissionByTypeOnTable(index?: number): boolean {
		const automaticRaw = this.externalItem?.automatic ?? false;

		// Detecta se a inconsistência vinculada é automática
		const isAutomatic =
			automaticRaw === 1 ||
			automaticRaw === true ||
			(typeof automaticRaw === 'string' && automaticRaw.toLowerCase().includes('auto'));

		const hasHandler = this.PermissionService.hasPermission('inconsistency_handler');
		const hasApprover = this.PermissionService.hasPermission('inconsistency_approver');

		// Para inconsistências automáticas: exige as duas permissões
		if (isAutomatic) {
			return hasHandler && hasApprover;
		}

		return hasHandler || hasApprover;
	}

	disabledFromStatus(index?: number): boolean {
		//Se você não tem external item, significa que é a criação de um novo item
		if (
			!this.externalItem ||
			this.externalItem.inconsistency_status_name === 'Awaiting approval' ||
			this.externalItem.inconsistency_status_name === ''
		) {
			return false;
		}
		return true;
	}

	confirmButton(elementReference: HTMLElement, e: Event): void {
		e.stopPropagation();
		this.NotificationService.setNotificationMessage(this.labels.lbl_clique_confirmar_acao);
		elementReference.classList.add('confirmClick');
		setTimeout(() => {
			elementReference.classList.remove('confirmClick');
		}, 5000);
	}

	separateCellMarkupFromValue(cellText: any, side: 'cellMarkup' | 'cellValue'): string {
		const markupRegex = /§([^§]+)§([^§]+)/;
		if (cellText == undefined || cellText == '') return '';
		cellText = cellText.toString();
		const sCellText = cellText.split('§');
		if (side == 'cellValue') return sCellText.at(-1);
		if (side == 'cellMarkup' && markupRegex.test(cellText)) {
			const markupArray = sCellText[1].split(';');
			let markupStyle = '{';
			markupArray.forEach((markup: string, index: number) => {
				const markupValue = markup.split(':')[markup.split(':').length - 1];
				if (markup.startsWith('fc')) {
					const cssVariablePattern = /^var\(--[\w-]+\)$/;
					const hexColorPattern = /^#([\dA-Fa-f]{3}|[\dA-Fa-f]{6})$/;
					if (cssVariablePattern.test(markupValue) || hexColorPattern.test(markupValue)) {
						markupStyle += `"color":"${markupValue}"`;
						if (index != markupArray.length - 1) markupStyle += ', ';
					}
				}

				if (markup.startsWith('fs')) {
					if (markupValue == 'i') {
						markupStyle += `"font-style": "italic"`;
						if (index != markupArray.length - 1) markupStyle += ', ';
					}
					if (markupValue == 'b') {
						markupStyle += `"font-weight": "bold"`;
						if (index != markupArray.length - 1) markupStyle += ', ';
					}
				}
			});
			markupStyle += '}';
			return JSON.parse(markupStyle);
		}

		return null;
	}

	navigateGoToCmdb(id: string) {
		const url = this.router.serializeUrl(this.router.createUrlTree(['/cmdb/' + id + '/edit']));
		window.open(url, '_blank');
	}

	toggleInconsistencyCiMarkAsSolved(idCi: string): void {
		const findCi = this.ciTableArr.find((ci) => ci.id == idCi);
		if (!findCi || this.isTogglingSolved.includes(idCi)) return;

		this.isTogglingSolved.push(idCi);
		this.InconsistenciesCiService.toggleInconsistencyCiMarkAsSolved(this.idInconsistency, idCi)
			.subscribe({
				next: () => {
					findCi.mark_as_solved = !findCi.mark_as_solved;
					this.NotificationService.setNotificationMessage('Informação de inconsistência atualizada com sucesso');
				},
				error: () => {
					this.NotificationService.setNotificationMessage('Erro ao atualizar informação de inconsistência');
				},
			})
			.add(() => {
				const index = this.isTogglingSolved.indexOf(idCi);
				if (index > -1) {
					this.isTogglingSolved.splice(index, 1);
				}
			});
	}
}
